import React, { useEffect, useMemo } from 'react';
import { connect, useSelector } from 'react-redux';
import { getOrganisation, clearOrganisation } from '../redux/organisationActions';
import { BackButton, Loader } from '../../../common/components';
import InternalContainer from '../../layout/containers/Internal';
import OrganisationDetails from '../components/OrganisationDetails/OrganisationDetails';
import OrganisationBrokers from '../components/OrganisationBrokers/OrganisationBrokers';
import OrganisationInsurers from '../components/OrganisationInsurers';
import OrganisationUsers from '../components/OrganisationUsers/OrganisationUsers';
import OwnerTheme from '../components/OwnerTheme/OwnerTheme';
import OwnerContent from '../components/OwnerContent/OwnerContent';
import OrganisationNotes from '../components/OrganisationNotes/OrganisationNotes';
import OrganisationAudit from '../components/OrganisationAudit';
import getIncludedResource from '../../../helpers/getIncludedResource';
import InternalTabs from '../../layout/components/InternalTabs/InternalTabs';
import { setActiveTab } from '../../layout/redux/tabs';

const TAB_NAME = 'organisationTabs';

const getTabs = (resource) => {
  if (!resource) {
    return [];
  }
  const organisationType = getIncludedResource(
    resource.data,
    resource.included,
    'organisation_type',
  );
  const organisationTypeName = organisationType.data.attributes.name;
  const organisationTabs = [
    {
      permissions: ['organisation.view'],
      title: 'Details',
      content: <OrganisationDetails resource={resource} />,
    },
    {
      type: 'Broker',
      permissions: ['broker.view'],
      title: 'Brokers/Branches',
      content: <OrganisationBrokers resource={resource} />,
    },
    {
      type: 'Insurer',
      permissions: ['organisation.view'],
      title: 'Brands',
      content: <OrganisationInsurers resource={resource} />,
    },
    {
      permissions: ['user.view'],
      title: 'Users/Contacts',
      content: <OrganisationUsers resource={resource} />,
    },
    {
      type: 'Owner',
      permissions: ['organisation.view'],
      title: 'Whitelabel',
      content: <OwnerTheme resource={resource} />,
    },
    {
      type: 'Owner',
      permissions: ['organisation.view'],
      title: 'Email and Letter Content',
      content: (
        <OwnerContent
          resource={resource}
          ownerId={resource.data.id}
          getOrganisation={getOrganisation}
        />
      ),
    },
    {
      permissions: ['organisation.notes_view'],
      title: 'Notes',
      content: <OrganisationNotes resource={resource} />,
    },
    {
      permissions: ['system.audit_logs_view'],
      orgType: ['owner'],
      title: 'Audit',
      content: <OrganisationAudit resource={resource} />,
    },
  ];

  return organisationTabs.filter((tab) => {
    return !(tab.type && tab.type !== organisationTypeName);
  });
};

const OrganisationShowContainer = ({
  onGetOrganisation,
  onSetActiveTab,
  onClearOrganisation,
  params: { id },
}) => {
  useEffect(
    () => {
      onGetOrganisation(id);
      onSetActiveTab(TAB_NAME, 1);
      return () => {
        onClearOrganisation();
      };
    },
    [id, onGetOrganisation, onSetActiveTab],
  );

  const resource = useSelector((state) => state?.organisation?.resources?.[id]);
  const tabs = useMemo(() => getTabs(resource), [resource]);

  const organisationType = getIncludedResource(
    resource?.data,
    resource?.included,
    'organisation_type',
  );
  const organisationTypeName = organisationType?.data?.attributes?.name;

  const organisationTypeDisplayName =
    organisationTypeName === 'Broker' ? 'Parent Broker' : organisationTypeName;

  if (!resource) {
    return (
      <InternalContainer title={'Organisation'} buttons={<BackButton />}>
        <Loader />
      </InternalContainer>
    );
  }

  return (
    <InternalContainer
      title={`Organisation ${organisationTypeDisplayName}`}
      buttons={<BackButton />}
    >
      <InternalTabs id={TAB_NAME} tabs={tabs} />
    </InternalContainer>
  );
};

export default connect(
  null,
  {
    onSetActiveTab: setActiveTab,
    onGetOrganisation: getOrganisation,
    onClearOrganisation: clearOrganisation,
  },
)(OrganisationShowContainer);
